<template>
  <b-overlay :show="loading" rounded="sm" no-fade>
    <b-card>
      <div class="d-flex align-items-center justify-content-between mb-2 flex-wrap">
        <h4 class="mb-0">{{ isEdit ? "Edit Governance Event" : "New Governance Event" }}</h4>
        <b-button variant="outline-secondary" size="sm" :to="{ name: 'governance-events' }">
          <feather-icon icon="ArrowLeftIcon" class="mr-50" />
          Back to list
        </b-button>
      </div>

      <validation-observer ref="eventRules">
        <b-form @submit.prevent="submit">
          <app-collapse accordion type="border">
            <!-- Details -->
            <app-collapse-item title="Event Details" :is-visible="true">
              <b-row>
                <b-col cols="12" md="8">
                  <b-form-group label="Title *" label-for="gov-title">
                    <validation-provider name="title" #default="{ errors }" rules="required">
                      <b-form-input id="gov-title" v-model="form.title" placeholder="e.g. Board Meeting - Q1" />
                      <small class="text-danger">{{ errors[0] }}</small>
                    </validation-provider>
                  </b-form-group>
                </b-col>
                <b-col cols="12" md="4">
                  <b-form-group label="Type *">
                    <v-select
                      v-model="form.event_type"
                      :options="typeOptions"
                      :reduce="(o) => o.value"
                      :clearable="false"
                    />
                  </b-form-group>
                </b-col>

                <b-col cols="12">
                  <div class="d-flex align-items-center justify-content-between mb-50">
                    <h6 class="mb-0">Parts</h6>
                    <b-form-checkbox v-model="form.is_all_day" switch>All day</b-form-checkbox>
                  </div>
                  <div
                    v-for="(p, idx) in form.parts"
                    :key="`part-${idx}`"
                    class="border rounded p-1 mb-1"
                  >
                    <div class="d-flex justify-content-between align-items-center">
                      <h6 class="mb-0">{{ getPartTitle(idx) }}</h6>
                      <b-button
                        v-if="form.parts.length > 1"
                        size="sm"
                        variant="outline-danger"
                        @click="removePart(idx)"
                      >Remove</b-button>
                    </div>
                    <b-row class="mt-50">
                      <b-col md="4">
                        <label>Start *</label>
                        <b-form-input type="date" v-model="p.start_date" />
                        <b-form-input
                          v-if="!form.is_all_day"
                          class="mt-25"
                          type="text"
                          v-model="p.start_time"
                          placeholder="HH:mm"
                          @blur="onTimeBlur(p, 'start_time')"
                        />
                      </b-col>
                      <b-col md="4">
                        <label>End</label>
                        <b-form-input type="date" v-model="p.end_date" />
                        <b-form-input
                          v-if="!form.is_all_day"
                          class="mt-25"
                          type="text"
                          v-model="p.end_time"
                          placeholder="HH:mm"
                          @blur="onTimeBlur(p, 'end_time')"
                        />
                      </b-col>
                      <b-col md="4">
                        <label>Venue</label>
                        <v-select
                          :options="venues"
                          label="name"
                          :reduce="(v) => v.id"
                          v-model="p.venue_id"
                          placeholder="Select venue"
                        />
                        <b-form-input
                          class="mt-25"
                          v-model="p.location"
                          placeholder="or other location / room"
                        />
                      </b-col>
                    </b-row>
                    <b-row class="mt-50">
                      <b-col md="8">
                        <label>Meeting URL</label>
                        <b-form-input v-model="p.meeting_url" placeholder="https://teams.microsoft.com/..." />
                      </b-col>
                      <b-col md="4">
                        <label>Passcode</label>
                        <b-form-input v-model="p.meeting_passcode" />
                      </b-col>
                    </b-row>
                  </div>
                  <b-button size="sm" variant="outline-primary" class="mb-1" @click="addPart">+ Add part</b-button>
                </b-col>

                <b-col cols="12" md="6">
                  <b-form-group label="Working group (optional)">
                    <v-select
                      v-model="form.working_group_id"
                      :options="workingGroups"
                      label="group_name"
                      :reduce="(o) => o.id"
                      placeholder="None"
                      @input="onWorkingGroupChange"
                    />
                    <small v-if="selectedWorkingGroup" class="text-muted">
                      Its contacts are added to the invitees automatically.
                    </small>
                  </b-form-group>
                </b-col>
                <b-col cols="12" md="6">
                  <b-form-group label="Event administrator *">
                    <v-select
                      v-model="form.admin_user_id"
                      :options="adminUsers"
                      label="title"
                      :reduce="(u) => u.id"
                      :clearable="false"
                      placeholder="Select a user"
                    />
                    <small class="text-muted">
                      Sees invitee responses and special requirements, and can record attendance.
                    </small>
                  </b-form-group>
                </b-col>
                <b-col cols="12" md="6">
                  <b-form-group label="Status *">
                    <v-select
                      v-model="form.status"
                      :options="statusOptions"
                      :reduce="(o) => o.value"
                      :clearable="false"
                    />
                  </b-form-group>
                </b-col>

                <b-col cols="12" v-if="form.status === 'cancelled'">
                  <b-form-group label="Cancellation reason">
                    <b-form-textarea v-model="form.cancelled_reason" rows="2" />
                  </b-form-group>
                </b-col>

                <b-col cols="12">
                  <b-form-group label="Description / agenda summary">
                    <b-form-textarea v-model="form.description" rows="4" />
                  </b-form-group>
                </b-col>
              </b-row>
            </app-collapse-item>

            <!-- Invitees -->
            <app-collapse-item title="Invitees" :is-visible="true">
              <b-form-group label="Invite people (they will see this event on their dashboard)">
                <v-select
                  v-model="form.invitee_ids"
                  :options="users"
                  label="title"
                  :reduce="(o) => o.id"
                  multiple
                  :close-on-select="false"
                  placeholder="Search users..."
                />
              </b-form-group>
              <div class="d-flex flex-wrap align-items-center">
                <b-button
                  v-for="role in inviteeRoles"
                  :key="role.key"
                  size="sm"
                  variant="outline-primary"
                  class="mr-50 mb-50"
                  @click="addByRole(role.key)"
                >+ All {{ role.label }}</b-button>
                <template v-if="selectedWorkingGroup">
                  <b-button
                    v-if="allWgContactsInvited"
                    size="sm"
                    variant="outline-danger"
                    class="mr-50 mb-50"
                    @click="removeWorkingGroupContacts(selectedWorkingGroup)"
                  >- Remove Working Group contacts</b-button>
                  <b-button
                    v-else
                    size="sm"
                    variant="outline-primary"
                    class="mr-50 mb-50"
                    :disabled="!wgInvitableIds.length"
                    @click="addWorkingGroupContacts(selectedWorkingGroup, true)"
                  >+ Add Working Group contacts</b-button>
                </template>
                <b-form-checkbox v-model="form.notify_invitees" switch class="mb-50 ml-1">
                  Email invitations (with calendar file)
                </b-form-checkbox>
              </div>
              <small class="text-muted d-block mt-50">
                {{ form.invitee_ids.length }} invitee(s) selected. Only members of the Governance module
                (roles with the Governance menu permission) can be invited.
              </small>
              <small v-if="selectedWorkingGroup" class="text-muted d-block">
                {{ selectedWorkingGroup.group_name }}: {{ wgInvitableIds.length }} contact(s)<span v-if="wgSkippedCount">,
                  {{ wgSkippedCount }} member(s) skipped because they are not in the Governance module</span>.
              </small>
            </app-collapse-item>

            <!-- Reminders -->
            <app-collapse-item title="Reminders">
              <b-row v-for="(r, idx) in form.reminders" :key="idx" class="align-items-end mb-50">
                <b-col cols="12" md="3">
                  <b-form-group label="Days before" class="mb-0">
                    <b-form-input v-model.number="r.days_before" type="number" min="0" max="365" />
                  </b-form-group>
                </b-col>
                <b-col cols="12" md="7">
                  <b-form-group label="Message (optional)" class="mb-0">
                    <b-form-input v-model="r.message" placeholder="Custom reminder text" />
                  </b-form-group>
                </b-col>
                <b-col cols="12" md="2">
                  <b-button variant="outline-danger" size="sm" @click="form.reminders.splice(idx, 1)">
                    <feather-icon icon="TrashIcon" size="14" />
                  </b-button>
                </b-col>
              </b-row>
              <b-button size="sm" variant="outline-primary" @click="form.reminders.push({ days_before: 1, message: '' })">
                <feather-icon icon="PlusIcon" class="mr-25" />
                Add reminder
              </b-button>
              <small class="text-muted d-block mt-50">Reminders are emailed each morning to invitees who have not declined.</small>
            </app-collapse-item>

            <!-- Materials -->
            <app-collapse-item title="Agenda, Minutes &amp; Materials">
              <p class="text-muted small" v-if="!isEdit">
                Files are uploaded once the event is saved; you can also add them later from the event page.
              </p>
              <b-row v-for="(m, idx) in pendingMaterials" :key="idx" class="align-items-end mb-50">
                <b-col cols="12" md="2">
                  <b-form-group label="Kind" class="mb-0">
                    <v-select v-model="m.kind" :options="kindOptions" :reduce="(o) => o.value" :clearable="false" />
                  </b-form-group>
                </b-col>
                <b-col cols="12" md="3">
                  <b-form-group label="Title" class="mb-0">
                    <b-form-input v-model="m.title" placeholder="e.g. Agenda" />
                  </b-form-group>
                </b-col>
                <b-col cols="12" md="3">
                  <b-form-group label="File" class="mb-0">
                    <b-form-file v-model="m.file" size="sm" placeholder="Choose file" />
                  </b-form-group>
                </b-col>
                <b-col cols="12" md="3">
                  <b-form-group label="or Link" class="mb-0">
                    <b-form-input v-model="m.external_url" placeholder="https://" />
                  </b-form-group>
                </b-col>
                <b-col cols="12" md="1">
                  <b-button variant="outline-danger" size="sm" @click="pendingMaterials.splice(idx, 1)">
                    <feather-icon icon="TrashIcon" size="14" />
                  </b-button>
                </b-col>
              </b-row>
              <b-button size="sm" variant="outline-primary" @click="pendingMaterials.push({ kind: 'material', title: '', file: null, external_url: '' })">
                <feather-icon icon="PlusIcon" class="mr-25" />
                Add material
              </b-button>
            </app-collapse-item>
          </app-collapse>

          <hr />
          <div class="d-flex justify-content-end">
            <b-button variant="outline-secondary" class="mr-1" :to="{ name: 'governance-events' }">Cancel</b-button>
            <b-button variant="primary" type="submit" :disabled="loading">
              {{ isEdit ? "Save changes" : "Create event" }}
            </b-button>
          </div>
        </b-form>
      </validation-observer>
    </b-card>
  </b-overlay>
</template>

<script>
import store from "@/store";
import router from "@/router";
import vSelect from "vue-select";
import { ValidationProvider, ValidationObserver } from "vee-validate";
import { required } from "@validations";
import AppCollapse from "@core/components/app-collapse/AppCollapse.vue";
import AppCollapseItem from "@core/components/app-collapse/AppCollapseItem.vue";
import { onUnmounted } from "@vue/composition-api";
import governanceMixin, { registerGovernanceStore, unregisterGovernanceStore } from "./governanceMixin";

/** Id of the signed-in user, from the session saved at login. */
function currentUserId() {
  try {
    const user = JSON.parse(localStorage.getItem("userData") || "null");
    return user && user.id ? Number(user.id) : null;
  } catch (e) {
    return null;
  }
}

function emptyPart() {
  return {
    start_date: "",
    start_time: "",
    end_date: "",
    end_time: "",
    venue_id: null,
    location: "",
    meeting_url: "",
    meeting_passcode: "",
  };
}

/** "9" -> "09:00", "930" -> "09:30", "9:5" -> "09:05"; returns null when it cannot be read. */
function normalizeTime(value) {
  if (!value) return null;
  const v = String(value).trim().replace(".", ":");
  let h;
  let m;
  if (v.includes(":")) {
    [h, m] = v.split(":");
  } else if (/^\d{3,4}$/.test(v)) {
    h = v.slice(0, v.length - 2);
    m = v.slice(-2);
  } else {
    h = v;
    m = "0";
  }
  h = parseInt(h, 10);
  m = parseInt(m || "0", 10);
  if (Number.isNaN(h) || Number.isNaN(m) || h < 0 || h > 23 || m < 0 || m > 59) return null;
  return `${String(h).padStart(2, "0")}:${String(m).padStart(2, "0")}`;
}

export default {
  name: "GovernanceEventForm",
  components: { vSelect, ValidationProvider, ValidationObserver, AppCollapse, AppCollapseItem },
  mixins: [governanceMixin],
  setup() {
    registerGovernanceStore();
    onUnmounted(() => unregisterGovernanceStore());
  },
  data() {
    return {
      required,
      loading: false,
      users: [],
      adminUsers: [],
      venues: [],
      workingGroups: [],
      enums: { event_types: [], event_statuses: [], material_kinds: [] },
      form: {
        title: "",
        description: "",
        event_type: "meeting",
        is_all_day: false,
        parts: [emptyPart()],
        status: "scheduled",
        working_group_id: null,
        // The person creating the event, until someone else is picked.
        admin_user_id: currentUserId(),
        cancelled_reason: "",
        notify_invitees: true,
        invitee_ids: [],
        reminders: [{ days_before: 1, message: "" }],
      },
      pendingMaterials: [],
      // Working group whose contacts were last added, so switching groups can swap them.
      previousWorkingGroupId: null,
    };
  },
  computed: {
    selectedWorkingGroup() {
      return this.workingGroups.find((w) => w.id === this.form.working_group_id) || null;
    },
    /** Members of the selected group who can be invited (Governance members only). */
    wgInvitableIds() {
      return this.workingGroupInvitableIds(this.selectedWorkingGroup);
    },
    wgSkippedCount() {
      const wg = this.selectedWorkingGroup;
      return wg ? (wg.member_ids || []).length - this.wgInvitableIds.length : 0;
    },
    allWgContactsInvited() {
      const ids = this.wgInvitableIds;
      return ids.length > 0 && ids.every((id) => this.form.invitee_ids.includes(id));
    },
    eventId() {
      return router.currentRoute.params.id || null;
    },
    /** Quick-add buttons: one per role present in the invitee list (chair spellings merged, admins skipped). */
    inviteeRoles() {
      const chairNames = ["Chairperson", "Chairpeson", "Chairman"];
      const skip = ["admin", "supermanager"];
      const seen = new Set();
      const roles = [];
      this.users.forEach((u) => {
        if (!u.role || skip.includes(u.role)) return;
        const key = chairNames.includes(u.role) ? "Chairperson" : u.role;
        if (seen.has(key)) return;
        seen.add(key);
        roles.push({ key, label: `${key}s` });
      });
      return roles;
    },
    isEdit() {
      return !!this.eventId;
    },
    typeOptions() {
      return (this.enums.event_types.length ? this.enums.event_types : Object.keys(this.governanceTypeLabels))
        .map((t) => ({ value: t, label: this.govTypeLabel(t) }));
    },
    statusOptions() {
      return (this.enums.event_statuses.length ? this.enums.event_statuses : ["draft", "scheduled", "cancelled", "completed"])
        .map((s) => ({ value: s, label: this.govCapitalize(s) }));
    },
    kindOptions() {
      return (this.enums.material_kinds.length ? this.enums.material_kinds : ["agenda", "minutes", "material"])
        .map((k) => ({ value: k, label: this.govCapitalize(k) }));
    },
  },
  mounted() {
    this.loading = true;
    store
      .dispatch("governance/fetchOptions")
      .then((res) => {
        this.users = res.data.users || [];
        this.adminUsers = res.data.admin_users || [];
        this.venues = res.data.venues || [];
        this.workingGroups = res.data.workingGroups || [];
        this.enums = res.data.enums || this.enums;
      })
      .then(() => (this.isEdit ? this.loadEvent() : null))
      .finally(() => {
        this.loading = false;
      });
  },
  methods: {
    loadEvent() {
      return store.dispatch("governance/getEvent", this.eventId).then((res) => {
        const e = res.data;
        this.form = {
          title: e.title,
          description: e.description || "",
          event_type: e.event_type,
          is_all_day: !!e.is_all_day,
          parts: this.partsFromEvent(e),
          status: e.status,
          working_group_id: e.working_group_id,
          admin_user_id: e.admin_user_id || e.created_by || currentUserId(),
          cancelled_reason: e.cancelled_reason || "",
          notify_invitees: e.notify_invitees !== false,
          invitee_ids: (e.invitees || []).map((i) => i.user_id),
          reminders: (e.reminders || []).filter((r) => !r.is_sent).map((r) => ({ days_before: r.days_before, message: r.message || "" })),
        };
        this.previousWorkingGroupId = e.working_group_id || null;
      });
    },
    workingGroupInvitableIds(wg) {
      if (!wg) return [];
      const allowed = new Set(this.users.map((u) => u.id));
      return (wg.member_ids || []).filter((id) => allowed.has(id));
    },
    addWorkingGroupContacts(wg, notify = false) {
      const ids = this.workingGroupInvitableIds(wg);
      const before = this.form.invitee_ids.length;
      this.form.invitee_ids = Array.from(new Set([...this.form.invitee_ids, ...ids]));
      const added = this.form.invitee_ids.length - before;
      if (notify || added) {
        this.govToast(
          added ? `${added} working group contact(s) added` : "Working group contacts are already invited",
          added ? "success" : "warning"
        );
      }
    },
    removeWorkingGroupContacts(wg, keepIds = []) {
      const remove = new Set(this.workingGroupInvitableIds(wg).filter((id) => !keepIds.includes(id)));
      const before = this.form.invitee_ids.length;
      this.form.invitee_ids = this.form.invitee_ids.filter((id) => !remove.has(id));
      return before - this.form.invitee_ids.length;
    },
    /** Selecting a group adds its contacts; switching groups swaps the previous group's contacts out. */
    onWorkingGroupChange(newId) {
      const previous = this.workingGroups.find((w) => w.id === this.previousWorkingGroupId);
      const next = this.workingGroups.find((w) => w.id === newId);
      if (previous && previous !== next) {
        this.removeWorkingGroupContacts(previous, this.workingGroupInvitableIds(next));
      }
      if (next) this.addWorkingGroupContacts(next);
      this.previousWorkingGroupId = newId || null;
    },
    partsFromEvent(e) {
      const split = (dt) => (dt ? { date: dt.substring(0, 10), time: dt.substring(11, 16) } : { date: "", time: "" });
      const source = e.parts && e.parts.length
        ? e.parts
        : [{ start_at: e.start_at, end_at: e.end_at, location: e.location, meeting_url: e.meeting_url }];
      return source.map((p) => {
        const s = split(p.start_at);
        const en = split(p.end_at);
        return {
          start_date: s.date,
          start_time: s.time,
          end_date: en.date,
          end_time: en.time,
          venue_id: p.venue_id || null,
          location: p.location || "",
          meeting_url: p.meeting_url || "",
          meeting_passcode: p.meeting_passcode || "",
        };
      });
    },
    addPart() {
      const last = this.form.parts[this.form.parts.length - 1];
      const part = emptyPart();
      // Default the new part to the previous part's place and link.
      if (last) {
        part.venue_id = last.venue_id;
        part.location = last.location;
        part.meeting_url = last.meeting_url;
        part.meeting_passcode = last.meeting_passcode;
      }
      this.form.parts.push(part);
    },
    removePart(idx) {
      this.form.parts.splice(idx, 1);
    },
    getPartTitle(idx) {
      const title = this.form.title || "";
      // If only one part, don't call it Part 1 (same as training events).
      if (this.form.parts.length <= 1) return title || "Event timing";
      return title ? `${title} - Part ${idx + 1}` : `Part ${idx + 1}`;
    },
    onTimeBlur(part, key) {
      const norm = normalizeTime(part[key]);
      if (norm) part[key] = norm;
    },
    /** Build the API parts list; returns { parts } or { error }. */
    buildPartsPayload() {
      const allDay = this.form.is_all_day;
      const parts = [];
      for (let i = 0; i < this.form.parts.length; i += 1) {
        const p = this.form.parts[i];
        const label = this.form.parts.length > 1 ? `Part ${i + 1}: ` : "";
        if (!p.start_date) return { error: `${label}Start date is required` };

        const startTime = allDay ? "00:00" : normalizeTime(p.start_time);
        if (!startTime) return { error: `${label}Start time is required (HH:mm)` };

        let endAt = null;
        const endDate = p.end_date || (p.end_time || allDay ? p.start_date : "");
        if (endDate) {
          const endTime = allDay ? "23:59" : normalizeTime(p.end_time);
          if (!endTime) return { error: `${label}End time is required when an end date is set (HH:mm)` };
          endAt = `${endDate} ${endTime}:00`;
        }
        const startAt = `${p.start_date} ${startTime}:00`;
        if (endAt && endAt < startAt) return { error: `${label}End time must be after start time` };

        parts.push({
          start_at: startAt,
          end_at: endAt,
          venue_id: p.venue_id || null,
          location: p.location || null,
          meeting_url: p.meeting_url || null,
          meeting_passcode: p.meeting_passcode || null,
        });
      }
      return { parts };
    },
    addByRole(role) {
      // The live database spells the chair role "Chairpeson"; match both.
      const names = role === "Chairperson" ? ["Chairperson", "Chairpeson", "Chairman"] : [role];
      const ids = this.users.filter((u) => names.includes(u.role)).map((u) => u.id);
      const merged = new Set([...this.form.invitee_ids, ...ids]);
      this.form.invitee_ids = Array.from(merged);
      if (!ids.length) this.govToast(`No active users with role "${role}"`, "warning");
    },
    submit() {
      this.$refs.eventRules.validate().then((ok) => {
        if (!ok) return;

        const built = this.buildPartsPayload();
        if (built.error) {
          this.govToast("Check the event parts", "danger", built.error);
          return;
        }
        this.loading = true;

        const payload = {
          ...this.form,
          parts: built.parts,
          reminders: this.form.reminders.filter((r) => r.days_before !== "" && r.days_before !== null),
        };

        const request = this.isEdit
          ? store.dispatch("governance/updateEvent", { id: this.eventId, data: payload })
          : store.dispatch("governance/createEvent", payload);

        request
          .then((res) => this.uploadPendingMaterials(res.data.id))
          .then((id) => {
            this.govToast(this.isEdit ? "Event updated" : "Event created");
            router.push({ name: "governance-event-view", params: { id } });
          })
          .catch((error) => {
            this.loading = false;
            this.govToast("Could not save event", "danger", this.govErrorMessage(error));
          });
      });
    },
    uploadPendingMaterials(id) {
      const uploads = this.pendingMaterials
        .filter((m) => m.file || m.external_url)
        .map((m) => {
          const fd = new FormData();
          fd.append("kind", m.kind || "material");
          fd.append("title", m.title || (m.file ? m.file.name : m.external_url));
          if (m.file) fd.append("file", m.file);
          if (m.external_url) fd.append("external_url", m.external_url);
          return store.dispatch("governance/uploadMaterial", { id, formData: fd });
        });
      return Promise.all(uploads).then(() => id);
    },
  },
};
</script>

<style lang="scss">
@import "@core/scss/vue/libs/vue-select.scss";
</style>
